import { useState, type FC, useEffect } from 'react'
import { IconPasskey, Button } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { AccountRecoveryStatus, accountRecoveryV2 } from '@egw/api'
import { createPasskeyCredentials, getIsMobileWithPlatformAuthenticator, registerPasskey, useAuth } from '@egw/auth'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { sendEvent } from '@egw/events'
import { SvgPasskeyAdded } from '@egw/assets'
import { useFlags } from '@egw/feature-flags'
import { Layout, PageHeader } from '../../../components'

const log = new Logger('Passkey Recovery - Create Passkey')

export const RecoveryPasskeyPage: FC = () => {
  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()
  const [passkeyPersisted, setPasskeyPersisted] = useState<boolean>(false)
  const { refreshToken } = useAuth()
  const { flags } = useFlags<FeatureFlags>()

  useEffect(() => {
    void refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCreatePasskey = async (): Promise<void> => {
    // Browser Requests the registration Credential Options Submit
    sendEvent('device_requestRegistrationCredentialOptions_submit', {
      eventSource: 'egWealth',
      category: 'passkey-recovery',
      subCategory: 'registration-options',
    })
    try {
      const isMobileWithPlatformAuthenticator = await getIsMobileWithPlatformAuthenticator()
      const publicKeyResponse = await accountRecoveryV2({
        isMobileWithPlatformAuthenticator,
      })

      // Browswer Requests the registration Credential Options => Success
      if (
        publicKeyResponse.__typename === 'AccountRecoveryResponseV2' &&
        publicKeyResponse.publicKeyCredentialCreationOptions
      ) {
        sendEvent('device_requestRegistrationCredentialOptions_success', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-options',
        })
        // Start the Credential Registration
        sendEvent('device_RegistrationCredential_start', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-credential',
        })
        // request registration credential options
        const passkeyCredentials = await createPasskeyCredentials(publicKeyResponse.publicKeyCredentialCreationOptions)

        if ((passkeyCredentials as unknown) instanceof Error) {
          sendEvent('device_RegistrationCredential_error', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'registration-credential',
            error: passkeyCredentials,
          })
        }

        sendEvent('device_RegistrationCredential_success', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-credential',
        })

        sendEvent('device_RegistrationCredential_submit', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-credential',
        })

        // Register Credential Options
        const response = await accountRecoveryV2({
          publicKeyCredentials: passkeyCredentials,
        })

        // Credential Registration Error
        if ((response as unknown) instanceof Error) {
          sendEvent('eg_passkeyRecovery_error', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'result',
            error: response,
          })
        }

        // Credential Registration Success
        if (
          response.__typename === 'AccountRecoveryResponseV2' &&
          (response.status === AccountRecoveryStatus.REGISTER_WEBAUTHN_SUCCESS ||
            response.status === AccountRecoveryStatus.REGISTER_WEBAUTHN)
        ) {
          // eslint-disable-next-line testing-library/no-debugging-utils
          log.debug('Registering passkey succesful')
          sendEvent('eg_passkeyRecovery_success', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'result',
          })
          setPasskeyPersisted(true)

          return
        }
        // Credential Registration ERROR
        else if (response.__typename === 'AccountRecoveryError') {
          sendEvent('eg_passkeyRecovery_fail', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'result',
            failureReason: response.errorType,
          })
        }
        // In review
      } else if (
        publicKeyResponse.__typename === 'AccountRecoveryResponseV2' &&
        publicKeyResponse.status === AccountRecoveryStatus.IN_REVIEW
      ) {
        sendEvent('device_requestRegistrationCredentialOptions_review', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-options',
        })
        navigate(WEALTH_ROUTES.RECOVERY_REVIEW.path)
        return
      } else if (publicKeyResponse.__typename === 'AccountRecoveryError') {
        // Request the registration Credential Options Failure
        sendEvent('device_requestRegistrationCredentialOptions_fail', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'registration-options',
          error: publicKeyResponse,
        })
      }

      // Error Scenarios navigates to Recovery Problem Page
      navigate(WEALTH_ROUTES.RECOVERY_PROBLEM.path)
    } catch (e) {
      log.error('Error while creating passkey', e)
      // Request the registration Credential Options Error
      sendEvent('device_requestRegistrationCredentialOptions_error', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'registration-options',
        error: e,
      })
      navigate(WEALTH_ROUTES.RECOVERY_PROBLEM.path)
    }
  }

  const handleCreatePasskeyJwt = async (): Promise<void> => {
    // Browser Requests the registration Credential Options Submit
    sendEvent('device_requestRegistrationCredentialOptions_submit', {
      eventSource: 'egWealth',
      category: 'passkey-recovery',
      subCategory: 'registration-options',
    })

    try {
      const registerPasskeyResponse = await registerPasskey()

      if (registerPasskeyResponse.__typename === 'RegisterWebAuthnSuccess') {
        // Change screen to passkey persisted
        setPasskeyPersisted(true)
      } else {
        // TODO: NAVIGATE TO ERROR PAGE
        navigate(WEALTH_ROUTES.RECOVERY_PROBLEM.path)
      }
    } catch (e) {
      log.error('Error while creating passkey', e)
      navigate(WEALTH_ROUTES.RECOVERY_PROBLEM.path)
    }
  }

  return (
    <Layout>
      {!passkeyPersisted ? (
        <>
          <PageHeader title={t('recovery.create_passkey_title')} />
          <p className="text-text-header typo-primary-md-semibold mt-xs mb-lg">
            {t('recovery.create_passkey_description')}
          </p>

          <IconPasskey className="mb-lg" />
          <p className="text-text-header typo-primary-base-semibold mb-2xs">
            {t('recovery.create_passkey_what_are_passkeys')}
          </p>
          <p className="text-text-meta typo-primary-base-regular mb-lg">
            {t('recovery.create_passkey_what_are_passkeys_description')}
          </p>
          <p className="text-text-header typo-primary-base-semibold mb-2xs">
            {t('recovery.create_passkey_where_saved')}
          </p>
          <p className="text-text-meta typo-primary-base-regular mb-xl">
            {t('recovery.create_passkey_where_saved_description')}
          </p>

          <div className="flex-1 md:hidden" />

          <Button
            testId="createPasskey_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              void handleCreatePasskeyJwt()
            }}
            // isLoading={startRegisterIsLoading || registerIsLoading}
          >
            {t('recovery.create_passkey_button')}
          </Button>
        </>
      ) : (
        <>
          <PageHeader title={t('recovery.passkey_created_title')} className="mb-md" />
          <SvgPasskeyAdded className="w-1/3 h-fit mb-lg" strokeColor="rgb(var(--color-green-500))" />
          <p className="typo-primary-base-semibold text-text-header mb-2xs">{t('recovery.better_sign_in')}</p>
          <p className="typo-primary-base text-text-meta mb-lg">{t('recovery.single_tap')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-2xs">{t('recovery.manage_passkey')}</p>
          <p className="typo-primary-base text-text-meta mb-lg">{t('recovery.crud_description')}</p>
          <div className="flex flex-grow md:hidden" />

          <Button
            testId="createPasskey_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              window.location.href = WEALTH_ROUTES.OVERVIEW.path
            }}
          >
            {t('recovery.continue')}
          </Button>
        </>
      )}
    </Layout>
  )
}
